import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';

const NAVY  = '#0D1B2A';
const GOLD  = '#C9A84C';
const LT_GOLD = '#E8D5A3';
const CREAM = '#FDFAF3';
const GREEN = '#065F46';
const RED   = '#991B1B';

const CEFR_LEVELS = ['A1','A2','B1','B2','C1','C2'];

export default function VerifyPage() {
  const { code } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    api.get(`/api/verify/${code}`)
      .then(res => { setData(res.data); setLoading(false); setTimeout(() => setRevealed(true), 80); })
      .catch(() => { setLoading(false); setTimeout(() => setRevealed(true), 80); });
  }, [code]);

  const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}) : '—';

  const linkedInShare = () => {
    if (!data) return;
    const text = encodeURIComponent(
      `I just achieved ${data.cefr_level} — ${data.cefr_name} English certification through ${data.school_name}!\n` +
      `Verify my achievement: https://speakiq.uk/verify/${code}\n` +
      `#EnglishLearning #CEFR #SpeakIQ #${data.school_name?.replace(/\s+/g,'')}`
    );
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://speakiq.uk/verify/${code}`)}&summary=${text}`, '_blank');
  };

  const telegramShare = () => {
    const text = encodeURIComponent(
      `I achieved ${data?.cefr_level} English certification! Verify: https://speakiq.uk/verify/${code}`
    );
    window.open(`https://t.me/share/url?url=${encodeURIComponent(`https://speakiq.uk/verify/${code}`)}&text=${text}`, '_blank');
  };

  if (loading) return (
    <div style={{ minHeight:'100vh', background:NAVY, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{
          width:48, height:48, border:`4px solid ${GOLD}`, borderTopColor:'transparent',
          borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 16px',
        }}/>
        <p style={{ color:'rgba(255,255,255,0.6)', fontSize:14 }}>Verifying certificate…</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  /* ── INVALID ──────────────────────────────────────────────────────── */
  if (!data || !data.valid) return (
    <div style={{ minHeight:'100vh', background:NAVY, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
        @keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-8px)}40%,80%{transform:translateX(8px)}}
      `}</style>
      <div style={{
        maxWidth:480, width:'100%', background:'#fff', borderRadius:20,
        overflow:'hidden', boxShadow:'0 30px 80px rgba(0,0,0,0.5)',
        animation: revealed ? 'fadeUp 0.5s ease both' : 'none',
      }}>
        <div style={{ background:'#FEE2E2', padding:'36px 32px', textAlign:'center', borderBottom:'1px solid #FECACA' }}>
          <div style={{ fontSize:56, lineHeight:1, animation:'shake 0.5s ease 0.2s both' }}>❌</div>
          <h2 style={{ color:RED, fontSize:22, fontWeight:800, margin:'16px 0 6px' }}>Certificate Not Verified</h2>
          <p style={{ color:'#7F1D1D', fontSize:14, margin:0 }}>This code does not match any issued certificate.</p>
        </div>
        <div style={{ padding:'24px 32px', textAlign:'center' }}>
          <p style={{ color:'#6B7280', fontSize:13, lineHeight:1.7, margin:'0 0 20px' }}>
            If you believe this is an error, please contact{' '}
            <a href="mailto:hello@speakiq.uk" style={{ color:GOLD, fontWeight:700 }}>hello@speakiq.uk</a>
          </p>
          <div style={{ background:'#FEF3C7', borderRadius:10, padding:'12px 16px', fontSize:12, color:'#92400E' }}>
            ⚠️ Forged or altered certificates will not pass verification. This verification attempt has been logged.
          </div>
        </div>
      </div>
    </div>
  );

  /* ── VALID ────────────────────────────────────────────────────────── */
  const curIdx = CEFR_LEVELS.indexOf(data.cefr_level);
  const pct = Math.round(((curIdx+1)/CEFR_LEVELS.length)*100);

  return (
    <div style={{ minHeight:'100vh', background:NAVY, padding:'32px 16px' }}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
        @keyframes popIn{0%{transform:scale(0.3);opacity:0}60%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}
        @keyframes glow{0%,100%{box-shadow:0 0 20px rgba(201,168,76,0.3)}50%{box-shadow:0 0 40px rgba(201,168,76,0.6)}}
        .share-btn:hover{transform:translateY(-2px);filter:brightness(1.1);}
        .share-btn{transition:all 0.2s;}
        .dl-btn:hover{background:#a8873a!important;}
        .dl-btn{transition:background 0.2s;}
      `}</style>

      <div style={{ maxWidth:680, margin:'0 auto' }}>

        {/* Header badge */}
        <div style={{ textAlign:'center', marginBottom:24, animation: revealed?'fadeUp 0.4s ease both':'none' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8,
            background:'rgba(201,168,76,0.15)', border:`1px solid rgba(201,168,76,0.4)`,
            borderRadius:20, padding:'6px 16px' }}>
            <span style={{ width:8,height:8,borderRadius:'50%',background:GOLD,display:'inline-block' }}/>
            <span style={{ color:GOLD, fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase' }}>
              SpeakIQ Certificate Verification
            </span>
          </div>
        </div>

        {/* Main card */}
        <div style={{
          background:'#fff', borderRadius:20, overflow:'hidden',
          boxShadow:'0 30px 80px rgba(0,0,0,0.5)',
          animation: revealed ? 'fadeUp 0.5s ease 0.1s both' : 'none',
        }}>

          {/* Green header */}
          <div style={{
            background:`linear-gradient(135deg, ${GREEN} 0%, #047857 100%)`,
            padding:'36px 32px', textAlign:'center',
            animation: revealed ? 'glow 2s ease 0.6s infinite' : 'none',
          }}>
            <div style={{
              fontSize:60, lineHeight:1, marginBottom:16,
              animation: revealed ? 'popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.3s both' : 'none',
            }}>✅</div>
            <h2 style={{ color:'#fff', fontSize:24, fontWeight:800, margin:'0 0 6px' }}>
              Authentic Certificate
            </h2>
            <p style={{ color:'rgba(255,255,255,0.8)', fontSize:14, margin:0 }}>
              This certificate has been verified against the SpeakIQ database
            </p>
          </div>

          {/* Body */}
          <div style={{ padding:'32px' }}>

            {/* Student name + level */}
            <div style={{ textAlign:'center', marginBottom:28, paddingBottom:28,
              borderBottom:`2px solid ${LT_GOLD}` }}>
              <p style={{ color:'#6B7280', fontSize:12, fontWeight:600, textTransform:'uppercase',
                letterSpacing:1.5, margin:'0 0 6px' }}>Certificate Awarded To</p>
              <h3 style={{ color:NAVY, fontSize:30, fontWeight:800, fontStyle:'italic', margin:'0 0 12px',
                fontFamily:'Georgia, serif' }}>{data.student_name}</h3>
              <div style={{ display:'inline-flex', alignItems:'center', gap:10,
                background:`linear-gradient(135deg, ${GOLD} 0%, #a8873a 100%)`,
                borderRadius:10, padding:'8px 20px' }}>
                <span style={{ color:'#fff', fontSize:22, fontWeight:900 }}>{data.cefr_level}</span>
                <span style={{ color:'rgba(255,255,255,0.85)', fontSize:14, fontWeight:600 }}>
                  {data.cefr_name}
                </span>
              </div>
            </div>

            {/* Details grid */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:24 }}>
              {[
                ['Issued By',        data.school_name],
                ['Date of Issue',    fmtDate(data.issued_at)],
                ['Certificate No.',  `#${String(data.certificate_number||0).padStart(5,'0')}`],
                ['Verification Code',data.verification_code],
              ].map(([label, val]) => (
                <div key={label} style={{
                  background:CREAM, borderRadius:10, padding:'12px 14px',
                  border:`1px solid ${LT_GOLD}`,
                }}>
                  <p style={{ color:'#9CA3AF', fontSize:10, fontWeight:700, textTransform:'uppercase',
                    letterSpacing:1, margin:'0 0 4px' }}>{label}</p>
                  <p style={{ color:NAVY, fontSize:13, fontWeight:700, margin:0,
                    fontFamily: label==='Verification Code'?'monospace':'inherit' }}>{val}</p>
                </div>
              ))}
            </div>

            {/* CEFR bar */}
            <div style={{ marginBottom:24 }}>
              <p style={{ color:'#6B7280', fontSize:11, fontWeight:600, textTransform:'uppercase',
                letterSpacing:1, margin:'0 0 8px' }}>CEFR Journey</p>
              <div style={{ display:'flex', gap:3 }}>
                {CEFR_LEVELS.map((lv,i) => (
                  <div key={lv} style={{
                    flex:1, height:8, borderRadius:4,
                    background: i===curIdx ? GOLD : i<curIdx ? LT_GOLD : '#E9EBF0',
                    position:'relative',
                  }}>
                    <span style={{
                      position:'absolute', top:10, left:'50%', transform:'translateX(-50%)',
                      fontSize:9, color: i===curIdx?GOLD:i<curIdx?'#8B7337':'#9CA3AF',
                      fontWeight: i===curIdx?800:500,
                    }}>{lv}</span>
                  </div>
                ))}
              </div>
              <p style={{ color:'#9CA3AF', fontSize:11, marginTop:22, textAlign:'right' }}>
                {pct}% of the way to C2 mastery
              </p>
            </div>

            {/* Verification stats */}
            <div style={{
              background:'#F8FAFC', borderRadius:10, padding:'12px 16px', marginBottom:24,
              border:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', alignItems:'center',
            }}>
              <div>
                <p style={{ color:'#6B7280', fontSize:11, margin:'0 0 2px' }}>Times verified</p>
                <p style={{ color:NAVY, fontWeight:800, fontSize:18, margin:0 }}>{data.verification_count}</p>
              </div>
              <div style={{ textAlign:'right' }}>
                <p style={{ color:'#6B7280', fontSize:11, margin:'0 0 2px' }}>Last checked</p>
                <p style={{ color:NAVY, fontWeight:600, fontSize:12, margin:0 }}>{fmtDate(data.last_verified_at)}</p>
              </div>
              <div style={{
                background:`linear-gradient(135deg,${GREEN},#047857)`,
                borderRadius:8, padding:'6px 12px',
                color:'#fff', fontSize:11, fontWeight:700,
              }}>🔒 Verified</div>
            </div>

            {/* Verification only — this page confirms authenticity. It does not
                offer the certificate itself for download or sharing. */}
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div style={{ textAlign:'center', padding:'12px', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, color:'#166534', fontSize:13, fontWeight:600 }}>
                ✓ This is an authentic certificate issued by {data.school_name} via SpeakIQ.
              </div>
              <button onClick={() => { navigator.clipboard.writeText(`https://speakiq.uk/verify/${code}`); }} className="share-btn"
                style={{
                  padding:'11px', background:'#F3F4F6', color:NAVY, border:`1px solid #E5E7EB`,
                  borderRadius:10, fontWeight:700, fontSize:13, cursor:'pointer',
                }}>
                🔗 Copy this verification link
              </button>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            background:NAVY, padding:'16px 32px',
            display:'flex', justifyContent:'space-between', alignItems:'center',
          }}>
            <span style={{ color:'rgba(255,255,255,0.4)', fontSize:11 }}>
              © 2026 SpeakIQ English Platform
            </span>
            <span style={{ color:GOLD, fontSize:11, fontWeight:700 }}>
              speakiq.uk
            </span>
          </div>
        </div>

        {/* Legal note */}
        <p style={{ textAlign:'center', color:'rgba(255,255,255,0.3)', fontSize:11, marginTop:20 }}>
          This is a course achievement certificate. It does not constitute an official international qualification.
          Issued by {data.school_name} via SpeakIQ English Platform.
        </p>
      </div>
    </div>
  );
}
